import { useMutation } from "@tanstack/react-query"
import { DictType, Task } from "@pma/types"
import { PauseIcon, PlayIcon } from "lucide-react"
import { Button } from "@components/common/button"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { useContext, useEffect, useMemo, useState } from "react"
import TaskSessions from "./task-sessions"
import useTranslator from "@hooks/use-translator"
import { cn, formatTime } from "@pma/utils/helpers"
import { useAuth } from "@stores/auth-store"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { EDIT_TASK_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import axios from "axios"
import { addTaskSession, finishTaskSession, getTotalTime } from "@pma/utils/utils"
import { toast } from "@pma/hooks/use-toast"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"

interface Props {
  task: DictType
  revalidate?: CallableFunction
  parentTaskId?: number
}
const TimeTracker = ({ task, parentTaskId }: Props) => {
  const [sessionId, setSessionId] = useState<undefined | number>(undefined)
  const [time, setTime] = useState(0)
  const [timerOn, setTimerOn] = useState(false)
  const [openLogs, setOpenLogs] = useState(false)

  const { translate, locale } = useTranslator()
  const { setTaskExpandForLoading } = useContext(GroupedTasksContext)
  const { authConfig } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const newParams = new URLSearchParams(searchParams)

  const refreshKey = searchParams.get("revalidate")
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const taskId = task?.id

  let userId = authConfig?.id

  const isHebrew = locale === "he"

  const addTaskSessionMutation = useMutation({
    mutationKey: ["add-task-session"],
    mutationFn: addTaskSession,
    onSuccess: (data) => {
      // update sessions
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      setTimerOn(true)
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any)
      }
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      setTimerOn(false)
      setTime(0)
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to add session"),
        variant: "error",
      })
      // revalidate task information
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any)
      }
    },
  })

  const finishTaskSessionMutation = useMutation({
    mutationKey: ["finish-task-session", sessionId, userId],
    mutationFn: finishTaskSession,
    onSuccess: (data) => {
      setTimerOn(false)
      updateTaskEndDateMutation.mutate()
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any)
      }
    },
    onError: (error) => {
      // revalidate task information
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
    },
  })

  const updateTaskEndDate = async () => {
    let payload = {
      task_id: task?.id,
      user_id: userId,
    }

    let updates

    updates = {
      finish_to: new Date(),
    }

    const response = await axios.post(
      SERVER_URL + EDIT_TASK_ENDPOINT,
      { ...payload, ...updates },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )

    return response.data
  }

  const updateTaskEndDateMutation = useMutation({
    mutationKey: [`update-task-end-date`, task?.id],
    mutationFn: updateTaskEndDate,
    onSuccess: (data) => {
      // Revalidate tasks details
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any)
      }
    },
  })

  const hasActiveSession = useMemo(
    () => task?.active_session_number === 1,
    [task?.active_session_number, refreshKey],
  )

  const handleClick = () => {
    if (timerOn) {
      finishTaskSessionMutation.mutate({
        sessionId: task?.active_session?.id,
        userId,
      })
    } else {
      setTimerOn(true)
      addTaskSessionMutation.mutate({
        task_id: taskId,
        active: true,
        start: Date.now(),
      })

      setOpenLogs(true)
    }
  }

  useEffect(() => {
    // Tracks the duration
    let intervalId: NodeJS.Timeout

    if (timerOn) {
      intervalId = setInterval(() => {
        setTime((prevTime) => prevTime + 1000)
      }, 1000)
    }

    return () => clearInterval(intervalId)
  }, [timerOn, task?.id])

  useEffect(() => {
    // Sets up the timer
    setTime((prev) => 0)

    if (hasActiveSession) {
      const timeDiff = Date.now() - task?.active_session_start
      setSessionId((prev) => task?.active_session?.id)
      setTime((prev) => prev + getTotalTime(task?.sessions) + timeDiff)

      setTimerOn(true)
    } else {
      if (task?.sessions?.length > 0) {
        setTime((prev) => prev + getTotalTime(task?.sessions))
      } else {
        setTime(0)
      }
      setTimerOn(false)
    }
  }, [hasActiveSession, task?.sessions?.length])

  return (
    <Popover open={openLogs} onOpenChange={setOpenLogs}>
      <PopoverTrigger asChild className='cursor-pointer'>
        <div
          className={cn("flex w-full cursor-pointer items-center gap-0", isHebrew ? "rtl" : "ltr")}
        >
          <Button
            variant={"ghost"}
            className='m-0 p-0 hover:bg-transparent'
            onClick={handleClick}
            leftSection={
              timerOn ? (
                <PauseIcon size={14} className='h-5 w-5 rounded-full bg-primary p-1 text-white' />
              ) : (
                <PlayIcon
                  size={14}
                  className={cn(
                    "h-5 w-5 rounded-full bg-primary p-1 text-white",
                    isHebrew && "rotate-180",
                  )}
                />
              )
            }
          />

          <small className='px-1'>{formatTime(time, {})}</small>
        </div>
      </PopoverTrigger>
      <PopoverContent className='w-full md:w-[400px]'>
        <div className='flex justify-between space-x-4'>
          <div className='w-full space-y-1'>
            <h4 className='text-sm font-semibold'>{translate("Time Tracking Log")}</h4>

            {task?.sessions?.length > 0 && (
              <TaskSessions
                activeTime={
                  timerOn ? formatTime(time - getTotalTime(task?.sessions), {}) : undefined
                }
                sessions={task?.sessions}
                parentTaskId={parentTaskId}
              />
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default TimeTracker
